import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import ServerUnavailable from "@/pages/ServerUnavailable";
import {
  API_REACHABLE_EVENT,
  API_UNREACHABLE_EVENT,
  pingServer,
} from "@/services/serverStatus";

const RETRY_INTERVAL_SECONDS = 15;


export function ServerStatusGate({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [isDown, setIsDown] = useState(false);
  const [isChecking, setIsChecking] = useState(false);
  const [secondsToRetry, setSecondsToRetry] = useState<number | null>(null);
  const [isOnline, setIsOnline] = useState(() => navigator.onLine);

  
  const isDownRef = useRef(false);
  const checkInFlight = useRef(false);

  const check = useCallback(async () => {
    if (checkInFlight.current) return;
    checkInFlight.current = true;
    setIsChecking(true);

    try {
      const isUp = await pingServer();
      const wasDown = isDownRef.current;
      isDownRef.current = !isUp;
      setIsDown(!isUp);

      if (wasDown && isUp) queryClient.invalidateQueries();
    } finally {
      checkInFlight.current = false;
      setIsChecking(false);
    }
  }, [queryClient]);

  useEffect(() => {
    void check();
  }, [check]);

  useEffect(() => {
    const onUnreachable = () => {
      if (!isDownRef.current) void check();
    };
    const onReachable = () => {
      if (isDownRef.current) void check();
    };

    window.addEventListener(API_UNREACHABLE_EVENT, onUnreachable);
    window.addEventListener(API_REACHABLE_EVENT, onReachable);
    return () => {
      window.removeEventListener(API_UNREACHABLE_EVENT, onUnreachable);
      window.removeEventListener(API_REACHABLE_EVENT, onReachable);
    };
  }, [check]);

  useEffect(() => {
    const onOnline = () => {
      setIsOnline(true);
      void check();
    };
    const onOffline = () => setIsOnline(false);

    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    return () => {
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
    };
  }, [check]);

  useEffect(() => {
    if (!isDown) {
      setSecondsToRetry(null);
      return;
    }

    setSecondsToRetry(RETRY_INTERVAL_SECONDS);
    const tick = window.setInterval(() => {
      setSecondsToRetry((remaining) =>
        remaining === null || remaining <= 1 ? RETRY_INTERVAL_SECONDS : remaining - 1,
      );
    }, 1000);
    const retry = window.setInterval(() => void check(), RETRY_INTERVAL_SECONDS * 1000);

    return () => {
      window.clearInterval(tick);
      window.clearInterval(retry);
    };
  }, [isDown, check]);

  if (isDown) {
    return (
      <ServerUnavailable
        onRetry={() => void check()}
        isRetrying={isChecking}
        retryInSeconds={secondsToRetry}
        isOnline={isOnline}
      />
    );
  }

  return <>{children}</>;
}

export default ServerStatusGate;
